import { FC, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useLocalize } from '@treams/treams-hooks/useLocalize';
import * as graphql from '@treams/treams-network';
import { GraphQLErrorMessage } from '@treams/treams-ui/common/GraphQLErrorMessage';
import { Jumbo } from '@treams/treams-ui/common/Jumbo';
import { Skeleton } from '@treams/treams-ui/common/Skeleton';

import CareerDetailBody from '../components/CareerDetailBody';
import CareerDetailHeader from '../components/CareerDetailHeader';
import { GrowthPathDetailBodySkeleton } from '../components/GrowthPathDetailBody';
import GrowthPathHeader from '../components/GrowthPathHeader';
import { useGrowthPathContext } from '../contexts/GrowthPathContext';
import { GrowthPathDetailContainer } from '../pages/GrowthPath';

const CareerDetail: FC = () => {
	const { localize } = useLocalize();
	const history = useHistory();

	const { userID, careerID, jobTitleID } = useGrowthPathContext();

	const { data, loading, error } = graphql.useCareerQuery({
		variables: { id: careerID },
		fetchPolicy: 'cache-and-network',
	});

	useEffect(() => {
		history.push(
			userID
				? `/users/${userID}/growth-path/career/${careerID}/job-title/${jobTitleID}`
				: `/growth-path/career/${careerID}/job-title/${jobTitleID}`,
		);
	}, [jobTitleID]);

	if (loading) {
		return <CareerDetailSkeleton />;
	}
	if (error) {
		return <GraphQLErrorMessage error={error.graphQLErrors[0]} />;
	}

	return (
		<GrowthPathDetailContainer>
			<GrowthPathHeader
				title={localize(data!.career.title).text}
				extra={<CareerDetailHeader career={data!.career} />}
			/>

			{jobTitleID && <CareerDetailBody />}
		</GrowthPathDetailContainer>
	);
};

const CareerDetailSkeleton = () => {
	return (
		<>
			<Jumbo.Skeleton wide>
				<Skeleton.Box width="40%" height={20} mt={2} />
			</Jumbo.Skeleton>
			<Box px={4}>
				<Flex>
					<Box width={260} />
					<Box>
						<Skeleton.Box width={150} height={24} mb={3} />
						<Skeleton.Box width={513} height={100} />
					</Box>
				</Flex>
			</Box>
			<GrowthPathDetailBodySkeleton />
		</>
	);
};

export default CareerDetail;
